import * as RadixPopover from '@radix-ui/react-popover';
import type { MouseEvent, ReactNode } from 'react';
import React from 'react';

import { cn } from '~/lib/cn';

type ContentType =
  | ReactNode
  | ((onItemClick: (event?: MouseEvent<HTMLElement>) => void) => ReactNode);

type PopoverProps = {
  content: ContentType;
  children: ReactNode;
  disabled?: boolean;
  /**
   * Prevents propagation of the trigger click event
   */
  stopPropagation?: boolean;

  triggerAsChild?: boolean;
  side?: RadixPopover.PopoverContentProps['side'];
  sideOffset?: RadixPopover.PopoverContentProps['sideOffset'];
  align?: RadixPopover.PopoverContentProps['align'];
  alignOffset?: RadixPopover.PopoverContentProps['alignOffset'];

  contentClassName?: string;
  onOpenChange?: (open: boolean) => void;
};

export const Popover = ({
  content,
  children,
  disabled,
  stopPropagation,
  triggerAsChild,
  contentClassName,
  side = 'right',
  sideOffset = 5,
  align,
  alignOffset,
  onOpenChange,
}: PopoverProps) => {
  const [isOpened, setIsOpened] = React.useState(false);

  const closePopover = () => {
    setIsOpened(false);
  };

  const triggerOnClick = (e: MouseEvent<HTMLElement>) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
  };

  const ContentComponent =
    typeof content === 'function' ? content(closePopover) : content;

  const handleOpenChange = (open: boolean) => {
    setIsOpened(open);
    onOpenChange?.(open);
  };

  return (
    <RadixPopover.Root open={isOpened} onOpenChange={handleOpenChange}>
      <RadixPopover.Anchor />
      <RadixPopover.Trigger
        disabled={disabled}
        onClick={triggerOnClick}
        asChild={triggerAsChild}
      >
        {children}
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          side={side}
          sideOffset={sideOffset}
          align={align}
          alignOffset={alignOffset}
          className={cn('z-50', contentClassName)}
        >
          {ContentComponent}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
};

Popover.displayName = 'Popover';
